import { vec2 } from "gl-matrix"
import { Renderer } from "./pipeline/renderer"
import { fitToSize } from "./utils/resize-image"
import { Photo } from "./photo"
import { throttled } from "@/utils/throttle"
import { PubSub } from "@/utils/pub-sub"
import { Texture } from "./backends/webgl/texture"
import { DualBuffers } from "./pipeline/dual-buffers"

export type HistogramEvent = "computed-histogram"

export interface HistogramOptions {
	isEnabled: boolean
	showHighlightsClipping?: boolean
	showShadowsClipping?: boolean
}

export class Histogram {
	/* Events */

	public events = new PubSub<HistogramEvent>()

	/* Options */

	private options: HistogramOptions

	/* Thumbnail */

	// Max possible histogram thumbnail size
	private static MaxSize: vec2 = [300, 300]
	private static ClippingColorIndicatorThreshold = 0.0

	// Renderer instance
	private renderer: Renderer

	// Thumbnail texture
	private outputTexture: Texture

	private renderPending = false
	private waitingSync = false

	private dualBuffers: DualBuffers

	// Thumbnail final data
	private textureData: Uint8Array = new Uint8Array(
		Histogram.MaxSize[0] * Histogram.MaxSize[1] * 4,
	)

	// Last generated histogram thumbnail size and other states
	//private lastPhotoId: string = ""
	//private lastAdjustmentsId: string = ""
	private lastSize: vec2 = [0, 0]

	/* Bins */

	// Max bins configuration
	public static Range = 256

	// Bins per color
	public red = new Float32Array(Histogram.Range)
	public green = new Float32Array(Histogram.Range)
	public blue = new Float32Array(Histogram.Range)

	// Max value for all bins
	public max: number = 0

	// Raw clipping percentages
	private shadowsClippingPercentage = 0.0
	private highlightsClippingPercentage = 0.0

	get isEnabled() {
		return this.options.isEnabled
	}

	get hasShadowsClipping() {
		return this.shadowsClippingPercentage > Histogram.ClippingColorIndicatorThreshold
	}

	get hasHighlightsClipping() {
		return this.highlightsClippingPercentage > Histogram.ClippingColorIndicatorThreshold
	}

	get showsHighlightsClipping() {
		return this.options.isEnabled && (this.options?.showHighlightsClipping ?? false)
	}

	get showsShadowsClipping() {
		return this.options.isEnabled && (this.options.showShadowsClipping ?? false)
	}

	public constructor(renderer: Renderer, options: HistogramOptions = { isEnabled: true }) {
		this.renderer = renderer
		this.options = options
		this.dualBuffers = new DualBuffers(renderer.backend, "histogram.dualBuffers")
		this.outputTexture = renderer.backend.createTexture({
			label: "histogram.outputTexture",
			format: "rgba8unorm",
		})
	}

	public setColorClipping(highlights?: boolean, shadows?: boolean) {
		if (highlights !== undefined) {
			this.options.showHighlightsClipping = highlights
		}
		if (shadows !== undefined) {
			this.options.showShadowsClipping = shadows
		}
	}

	public setEnabled(value: boolean) {
		this.options.isEnabled = value
	}

	@throttled(50)
	public update(photo: Photo) {
		if (!this.options.isEnabled) {
			return
		}

		this.renderPending = true

		if (this.waitingSync) {
			return
		}

		this.updateSync(photo)

		this.renderPending = false
		this.waitingSync = true

		this.renderer.backend.waitClientSync(() => {
			if (this.renderPending) {
				this.updateSync(photo)
				this.renderPending = false

				this.renderer.backend.waitClientSync(() => (this.waitingSync = false))
			} else {
				this.waitingSync = false
			}
		})
	}

	public dispose() {
		this.dualBuffers.reset()
		this.outputTexture.reset()
	}

	private updateSync(photo: Photo) {
		if (!this.options.isEnabled) {
			return
		}

		const size = fitToSize(photo.imageState.croppedImageSize, Histogram.MaxSize)

		if (size !== this.lastSize) {
			this.outputTexture.updateSize(size[0], size[1])
			this.lastSize = size
		}

		if (photo.texture.width === 0 || photo.texture.height === 0) {
			return
		}

		const output = this.renderer.render(photo, {
			width: size[0],
			height: size[1],
			ignoreCrop: false,
			buffers: this.dualBuffers,
			colorSpace: "gamma",
			output: { format: "rgba", texture: this.outputTexture },
			disableHealing: true,
			disableSuperSampling: true,
		})

		if (!output) {
			return
		}

		void output.readAsync({ bufferView: this.textureData, pollIntervalMs: 50 }).then(() => {
			this.computeHistogram()
			this.events.publish("computed-histogram")
		})

		//output.read({ bufferView: this.textureData })
	}

	private computeHistogram() {
		const pixels = this.textureData
		const bufferSize = this.lastSize[0] * this.lastSize[1] * 4

		for (let i = 0; i < Histogram.Range; ++i) {
			this.red[i] = 0
			this.green[i] = 0
			this.blue[i] = 0
		}

		let scp = 0 // count of zero
		let hcp = 0 // count of Rnage -1 (255)
		const maxHistogramValue = Histogram.Range - 1

		for (let i = 0; i < bufferSize; i += 4) {
			const _r = Math.round(((pixels[i] || 0) / 255.0) * maxHistogramValue)
			const _g = Math.round(((pixels[i + 1] || 0) / 255.0) * maxHistogramValue)
			const _b = Math.round(((pixels[i + 2] || 0) / 255.0) * maxHistogramValue)

			if (_r > 0 && _r < maxHistogramValue) {
				this.red[_r] = (this.red[_r] ?? 0) + 1
			}
			if (_g > 0 && _g < maxHistogramValue) {
				this.green[_g] = (this.green[_g] ?? 0) + 1
			}
			if (_b > 0 && _b < maxHistogramValue) {
				this.blue[_b] = (this.blue[_b] ?? 0) + 1
			}

			if (_r === 0 && _g === 0 && _b === 0) {
				scp += 1
			}

			if (_r === maxHistogramValue && _g === maxHistogramValue && _b === maxHistogramValue) {
				hcp += 1
			}
		} // for

		const pixelsCount = this.lastSize[0] * this.lastSize[1]

		this.shadowsClippingPercentage = scp / pixelsCount
		this.highlightsClippingPercentage = hcp / pixelsCount

		/* smooth - begin */
		// to get more reasonable smooth results on i = 1 and i = range - 2
		this.red[0] = this.red[1] || 0
		this.green[0] = this.green[1] || 0
		this.blue[0] = this.blue[1] || 0
		this.red[maxHistogramValue] = this.red[maxHistogramValue - 1] || 0
		this.green[maxHistogramValue] = this.green[maxHistogramValue - 1] || 0
		this.blue[maxHistogramValue] = this.blue[maxHistogramValue - 1] || 0

		this.smoothHistogram()
		/* smooth - end */

		let max = 0

		for (let i = 0; i < Histogram.Range; i++) {
			const _r = this.red[i] || 0
			const _g = this.green[i] || 0
			const _b = this.blue[i] || 0

			max = Math.max(max, _r, _g, _b)
		}

		this.max = max
	}

	private smoothHistogram() {
		// Create temporary arrays to store the smoothed values
		const smoothedRed = new Float32Array(Histogram.Range)
		const smoothedGreen = new Float32Array(Histogram.Range)
		const smoothedBlue = new Float32Array(Histogram.Range)

		// Gaussian-like kernel with peak preservation
		const kernel = [0.1, 0.2, 0.4, 0.2, 0.1]
		const kernelHalfWidth = Math.floor(kernel.length / 2)
		const maxHistogramValue = Histogram.Range - 1

		for (let i = 1; i < maxHistogramValue; i++) {
			let sumR = 0,
				sumG = 0,
				sumB = 0
			let weightSum = 0

			for (let k = -kernelHalfWidth; k <= kernelHalfWidth; k++) {
				const idx = Math.max(0, Math.min(maxHistogramValue, i + k))
				const weight = kernel[k + kernelHalfWidth] || 0

				sumR += (this.red[idx] || 0) * weight
				sumG += (this.green[idx] || 0) * weight
				sumB += (this.blue[idx] || 0) * weight
				weightSum += weight
			}

			smoothedRed[i] = sumR / weightSum
			smoothedGreen[i] = sumG / weightSum
			smoothedBlue[i] = sumB / weightSum

			// don't alter the value too much to preserve peaks/valleys
			const thresh = 25
			const deltaR = (smoothedRed[i] as number) - (this.red[i] as number)
			if (Math.abs(deltaR) > thresh) {
				const sign = deltaR > 0 ? 1 : -1
				smoothedRed[i] =
					(this.red[i] || 0) + sign * ((Math.abs(deltaR) - thresh) * 0.2 + thresh)
			}

			const deltaG = (smoothedGreen[i] as number) - (this.green[i] as number)
			if (Math.abs(deltaG) > thresh) {
				const sign = deltaG > 0 ? 1 : -1
				smoothedGreen[i] =
					(this.green[i] || 0) + sign * ((Math.abs(deltaG) - thresh) * 0.2 + thresh)
			}

			const deltaB = (smoothedBlue[i] as number) - (this.blue[i] as number)
			if (Math.abs(deltaB) > thresh) {
				const sign = deltaB > 0 ? 1 : -1
				smoothedBlue[i] =
					(this.blue[i] || 0) + sign * ((Math.abs(deltaB) - thresh) * 0.2 + thresh)
			}
		}

		// Copy smoothed values back to the original arrays and capped at 1600;
		// the first and last values are set to 0.
		const maxValue = 1600
		for (let i = 0; i < Histogram.Range; i++) {
			this.red[i] = Math.min(smoothedRed[i] || 0, maxValue)
			this.green[i] = Math.min(smoothedGreen[i] || 0, maxValue)
			this.blue[i] = Math.min(smoothedBlue[i] || 0, maxValue)
		}
	}
}
